import React from 'react'

// NOTE: reusable hook to fetch the data from the server
// by fetchHandler function (remember to pass a memoized
// version of fetchHandler to avoid infinite loop),
// it also allows to use it as a local state storage,
// the state can be also mutated with exposed handlers.
// apiFetch resolves to { error } instead of rejecting, and a handler
// returns undefined or null when there is nothing to fetch yet
interface FetchResult<T> {
  error?: unknown
  meta?: unknown
  response?: T
}

export const useFetch = <T = unknown>(
  fetchHandler: () => Promise<FetchResult<T> | null | undefined>,
) => {
  const [error, setError] = React.useState<unknown>(null)
  const [meta, setMeta] = React.useState<unknown>(null)
  const [response, setResponse] = React.useState<T | null>(null)
  const [isFetching, setIsFetching] = React.useState(true)

  const handleRefetch = React.useCallback(async () => {
    try {
      setIsFetching(true)
      const result = await fetchHandler()
      if (!result) return
      const { error = null, meta, response } = result
      setError(error)
      setMeta(meta)
      setResponse(response ?? null)
    } catch (err) {
      setError(err)
    } finally {
      setIsFetching(false)
    }
  }, [fetchHandler])

  React.useEffect(() => {
    handleRefetch()
  }, [handleRefetch])

  return {
    state: { error, meta, response, isFetching },
    handlers: {
      handleRefetch,
      handleSetError: setError,
      handleSetMeta: setMeta,
      handleSetResponse: setResponse,
    },
  }
}
